import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Sparkles, CreditCard, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

export default function BuyCreditsPage() {
  const { t, i18n } = useTranslation();
  const { api, user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isSpanish = i18n.language === 'es';
  
  const [loading, setLoading] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  
  const restaurantId = searchParams.get('restaurant');
  const preselectedPack = searchParams.get('pack');

  useEffect(() => {
    if (preselectedPack) {
      setSelectedPack(parseInt(preselectedPack));
    }
  }, [preselectedPack]);

  const packs = [
    {
      id: 5,
      credits: 5,
      price: 5,
      description: isSpanish ? 'Para uso ocasional' : 'For occasional use',
      popular: false
    },
    {
      id: 15,
      credits: 15,
      price: 12,
      originalPrice: 15,
      discount: '20%',
      description: isSpanish ? 'Más popular' : 'Most popular',
      popular: true
    },
    {
      id: 50,
      credits: 50,
      price: 35,
      originalPrice: 50,
      discount: '30%',
      description: isSpanish ? 'Mejor valor' : 'Best value',
      popular: false
    }
  ];

  const handlePurchase = async (packId) => {
    if (!restaurantId) {
      toast.error(isSpanish ? 'Restaurante no especificado' : 'Restaurant not specified');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`/restaurants/${restaurantId}/ai-credits/purchase`, {
        pack: packId
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(isSpanish ? 'Error al procesar la compra' : 'Failed to process purchase');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full text-purple-700 mb-4">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">AI Credits</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            {isSpanish ? 'Comprar Créditos AI' : 'Buy AI Credits'}
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            {isSpanish 
              ? 'Usa créditos AI para escanear recibos y menús automáticamente con inteligencia artificial'
              : 'Use AI credits to automatically scan receipts and menus with artificial intelligence'}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {packs.map((pack) => (
            <Card 
              key={pack.id}
              className={`relative cursor-pointer transition-all hover:shadow-lg ${
                selectedPack === pack.id ? 'ring-2 ring-purple-500 shadow-lg' : ''
              } ${pack.popular ? 'border-purple-500' : ''}`}
              onClick={() => setSelectedPack(pack.id)}
            >
              {pack.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-purple-500">
                    {isSpanish ? 'MÁS POPULAR' : 'MOST POPULAR'}
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-5xl font-bold text-slate-900">
                  {pack.credits}
                </CardTitle>
                <CardDescription>
                  {isSpanish ? 'escaneos' : 'scans'}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="text-center space-y-4">
                <div>
                  <span className="text-3xl font-bold text-slate-900">£{pack.price}</span>
                  {pack.originalPrice && (
                    <span className="ml-2 text-lg text-slate-400 line-through">£{pack.originalPrice}</span>
                  )}
                </div>
                
                {pack.discount && (
                  <Badge className="bg-green-500">{pack.discount} OFF</Badge>
                )}
                
                <p className="text-slate-600">{pack.description}</p>
                
                <div className="pt-4">
                  <Button 
                    className={`w-full ${pack.popular ? 'bg-purple-500 hover:bg-purple-600' : ''}`}
                    variant={pack.popular ? 'default' : 'outline'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePurchase(pack.id);
                    }}
                    disabled={loading}
                  >
                    {loading && selectedPack === pack.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    {isSpanish ? 'Comprar Ahora' : 'Buy Now'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
            {isSpanish ? '¿Qué Puedes Hacer con los Créditos AI?' : 'What Can You Do with AI Credits?'}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Check className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">
                  {isSpanish ? 'Escanear Recibos de Proveedores' : 'Scan Supplier Receipts'}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isSpanish 
                    ? 'Sube una foto del recibo y la AI extraerá automáticamente todos los productos y cantidades'
                    : 'Upload a photo of the receipt and AI will automatically extract all products and quantities'}
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Check className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">
                  {isSpanish ? 'Escanear Menús Existentes' : 'Scan Existing Menus'}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isSpanish 
                    ? 'Digitaliza tu menú en papel - la AI lee nombres, descripciones y precios'
                    : 'Digitize your paper menu - AI reads names, descriptions and prices'}
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Check className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">
                  {isSpanish ? 'Actualización Masiva de Stock' : 'Bulk Stock Updates'}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isSpanish 
                    ? 'Añade stock de múltiples productos con un solo escaneo de la factura'
                    : 'Add stock for multiple products with a single invoice scan'}
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Check className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">
                  {isSpanish ? 'Ahorra Tiempo' : 'Save Time'}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isSpanish 
                    ? 'Lo que antes tardaba horas ahora se hace en segundos con AI'
                    : 'What used to take hours now takes seconds with AI'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-slate-500 text-sm">
          <p>
            {isSpanish 
              ? '2 escaneos gratuitos incluidos cada mes. Los créditos comprados no caducan.'
              : '2 free scans included every month. Purchased credits never expire.'}
          </p>
        </div>
      </div>
    </div>
  );
}
